import { fromJS } from 'immutable'
import * as supportParameterActions from 'actions/supportParameter.js'
import * as referenceParameterActions from 'actions/referenceParameter.js'
import {
  slotIsOpen,
  slotIsShort,
  translateNativeRowIdToLSValue,
} from 'utils/supportParameter.js'
import {
  SLOT_EDIT_NONE,
  SLOT_EDIT_OPEN,
  SLOT_EDIT_SHORT,
  SLOT_EDIT_INPUT,
  SLOT_EDIT_OUTPUT,
  CARRY_ONLY,
  stringMaxVal,
} from 'utils/constants.js'

import { mLogicInputSet } from 'constants/spProperties'

import { set, setIn, safeToJS } from 'utils/immutable'
import { omit, compose, pick } from 'ramda'
import {
  gearValues,
  ignitionValues,
  lsIsIgnition,
  lsIsTransmission,
} from 'utils/logicalSlotConversions'
import { createReducer } from './utils'

const initialParam = {
  linkedRPs: [],
  isSearching: false,
  isEditing: false,
  descriptionIsValid: true,
  saveErrors: [],
  selectedSlot: null,
  outputSlotSelected: false,
  inputs: [],
  inputsFetching: false,
  outputs: [],
  outputsFetching: false,
  selectedOutput: null,
  selectedInput: null,
  slotEditDisplayMode: SLOT_EDIT_NONE,
  parameterGroupsFetching: false,
  parameterGroups: [],
}
const initialLogicalSlot = {
  selectedLogicalSlot: null,
}
export const initialState = fromJS({
  ...initialLogicalSlot,
  ...initialParam,
  dictionary: {},
  invalidSPValues: [],
  spBeingEdited: null,
})

const handlers = {
  [supportParameterActions.DUPLICATE_SUPPORT_PARAM]: duplicateSupportParam,
  [supportParameterActions.EDIT_SUPPORT_PARAM]: editSupportParam,
  [supportParameterActions.CANCEL_SUPPORT_PARAM_EDIT]: cancelSupportParamEdit,
  [supportParameterActions.UPDATE_SUPPORT_PARAM_FIELD]: updateSupportParamField,
  [supportParameterActions.SELECT_MLOGIC_EDIT_SLOT]: selectMlogicEditSlot,
  [supportParameterActions.SELECT_OUTPUT_SLOT_FOR_EDIT]: selectOutputSlotForEdit,
  [supportParameterActions.UPDATE_MLOGIC_SLOT_VALUE]: updateMlogicSlotValue,
  [supportParameterActions.SET_DETAIL_LEVEL]: setDetailLevel,
  [supportParameterActions.SET_SELECTED_LOGICAL_SLOT]: setSelectedLogicalSlot,
  [supportParameterActions.CLEAR_SELECTED_LOGICAL_SLOT]: clearSelectedLogicalSlot,
  [supportParameterActions.SET_SELECTED_INPUT]: setSelectedInput,
  [supportParameterActions.SET_SELECTED_OUTPUT]: setSelectedOutput,
  [supportParameterActions.SET_MLOGIC_OUTPUT_RESULT]: setMlogicOutputResult,
  [supportParameterActions.SET_SLOT_EDIT_DISPLAY_MODE]: setSlotEditDisplayMode,
  [supportParameterActions.SET_CURR_SP_FIELD_VALIDITY]: setCurrSpFieldValidity,
  [referenceParameterActions.GET_REFERENCE_PARAMETER]: getReferenceParameter,
  [referenceParameterActions.SAVE_REFERENCE_PARAM]: getReferenceParameter,
  [referenceParameterActions.UPDATE_RP_STATUS]: getSupportParametersFromRP,
  [referenceParameterActions.SOURCE_RP_SUPPORT_PARAMS_RECEIVED]: getSupportParametersFromRP,
  [referenceParameterActions.BATCH_IMPORT_COMPLETE]: getSupportParametersFromBatchImport,
  [supportParameterActions.GET_SUPPORT_PARAMETER]: getSupportParameterData,
  [supportParameterActions.SAVE_SUPPORT_PARAM]: saveSupportParameter,
  [supportParameterActions.ACTIVATE_SUPPORT_PARAM]: saveSupportParameter,
  [supportParameterActions.GET_LINKED_RPS]: getLinkedRPs,
  [supportParameterActions.SET_SLOT_PROPERTIES]: setSlotProperties,
  [supportParameterActions.SET_SELECTED_SLOT]: setSelectedSlot,
  [referenceParameterActions.FETCH_SUPPORT_PARAMS_SUCCESS]: getSupportParametersFromRP,
  [referenceParameterActions.CLEAR_RPS]: clearRps,
}

export default createReducer(initialState, handlers)

const updateSPInDictionary = (linkedRPUris, supportParameter) => {
  const { uri } = supportParameter
  return setIn(
    ['dictionary', uri],
    fromJS({
      linkedRPUris,
      ...supportParameter,
    })
  )
}
function getSupportParameterData(state, action) {
  const {
    result: uri,
    entities: { supportParameters },
  } = action.data
  const supportParameter = supportParameters[uri]
  const linkedRPUris = state.getIn(['dictionary', uri, 'linkedRPUris']) || []
  return updateSPInDictionary(linkedRPUris, supportParameter)(state)
}

function saveSupportParameter(state, { data }) {
  const {
    result: uri,
    entities: { supportParameters },
  } = data
  const supportParameter = supportParameters[uri]
  const linkedRPUris = state.getIn(['dictionary', uri, 'linkedRPUris']) || []
  const updateSP = updateSPInDictionary(linkedRPUris, supportParameter)
  const unsetSPBeingEdited = set('spBeingEdited', null)
  const setIsEditingFlagToFalse = set('isEditing', false)

  return compose(setIsEditingFlagToFalse, unsetSPBeingEdited, updateSP)(state)
}

function getLinkedRPs(
  state,
  { supportParameterNumber, data: { referenceParameters = [] } }
) {
  const spUri = `/support-parameters/${supportParameterNumber}`
  return setIn(
    ['dictionary', spUri, 'linkedRPUris'],
    fromJS(referenceParameters.map(({ uri }) => uri))
  )(state)
}

function duplicateSupportParam(state, { originalSupportParameter, data }) {
  const {
    result: supportParameter,
    entities: { supportParameters },
  } = data
  const sp = supportParameters[supportParameter]
  const { uri } = sp

  const currParam = {
    ...sp,
    originalSPUri: originalSupportParameter.uri,
  }

  const updateDictionary = setIn(['dictionary', uri], fromJS(currParam))

  const setIsEditing = set('isEditing', true)
  const setSPBeingEdited = set('spBeingEdited', currParam)

  return compose(setIsEditing, setSPBeingEdited, updateDictionary)(state)
}

function editSupportParam(state, { currentSPUri }) {
  const currParam = state.getIn(['dictionary', currentSPUri])

  return state.merge({
    spBeingEdited: currParam,
    isEditing: true,
  })
}

function cancelSupportParamEdit(state, action) {
  const { param } = action
  return state.merge({
    selectedSlot: null,
    spBeingEdited: null,
    isEditing: false,
    invalidSPValues: state
      .get('invalidSPValues')
      .filter((inv) =>
        !param
          ? false
          : !(inv.get('id') && String(inv.get('id')).includes(param.id))
      ),
  })
}

function updateSupportParamField(state, { fieldName, newValue }) {
  return state.setIn(['spBeingEdited', fieldName], newValue)
}

function selectMlogicEditSlot(state, { slot }) {
  return state.mergeDeep({
    selectedSlot: slot,
  })
}

function selectOutputSlotForEdit(state, action) {
  return state.merge({
    outputSlotSelected: true,
    selectedSlot: null,
    slotEditDisplayMode: SLOT_EDIT_OUTPUT,
  })
}

function updateMlogicSlotValue(state, action) {
  const selectedSlot = state.get('selectedSlot')
  let newState = {
    spBeingEdited: {
      inputs: {
        [selectedSlot]: {
          [action.fieldName]: action.newValue,
        },
      },
    },
  }

  if (action.fieldName === 'op') {
    const slotEditDisplayMode = getSlotEditDisplayMode(action.newValue)
    newState = { ...newState, slotEditDisplayMode }
  }

  return state.mergeDeep(newState)
}

function setDetailLevel(state, action) {
  return state.set('detailLevel', action.detailLevel)
}

function setSelectedLogicalSlot(state, action) {
  return state.merge({
    selectedLogicalSlot: fromJS(action.slot),
  })
}

function clearSelectedLogicalSlot(state, action) {
  return state.merge(initialLogicalSlot)
}

function setSelectedInput(state, { input, originalSPUri }) {
  const currSlotId = state.get('selectedSlot')
  const originalOp = state.getIn([
    'dictionary',
    originalSPUri,
    'inputs',
    currSlotId,
    'op',
  ])
  const currentOp = state.getIn(['spBeingEdited', 'inputs', currSlotId, 'op'])
  // if the current op is Open or Short/Closed, then find out if that was the original value. If so,
  // default it to Equals. If not, set it back to the original
  // TODO: Check to see if this comment ^^ is still applicable. If not, refactor code & delete comment
  /* eslint-disable no-nested-ternary */
  const setOp = ['0', '1'].includes(currentOp)
    ? ['0', '1'].includes(originalOp)
      ? '2'
      : originalOp
    : currentOp
  /* eslint-enable no-nested-ternary */

  // OMG WHY?!?!?!
  const ls =
    input.logicalSlot ||
    input.logical_slot ||
    input.timer_slot ||
    input.mlogic_slot_name ||
    input.mLogicSlotName ||
    translateNativeRowIdToLSValue(input)

  const displayText = input.name || input.description
  /* eslint-disable no-nested-ternary */
  const value = lsIsIgnition(ls)
    ? ignitionValues[0]
    : lsIsTransmission(ls)
    ? gearValues[0].value
    : // : String(CHEC_MAX_VALUE) unsure of the best default value should be
      '0'
  /* eslint-enable no-nested-ternary */

  return state.mergeDeep({
    selectedInput: fromJS(input),
    slotEditDisplayMode: SLOT_EDIT_INPUT,
    spBeingEdited: {
      inputs: {
        [currSlotId]: {
          ls,
          value,
          op: setOp,
          displayText: {
            title: displayText,
          },
        },
      },
    },
  })
}

function setSelectedOutput(state, action) {
  const { output } = action
  const { uri, description } = output
  const currentParameterOutput = safeToJS(
    state.getIn(['spBeingEdited', 'output'])
  )
  const updateOutputProperties =
    uri === stringMaxVal && description === CARRY_ONLY
      ? setIn(['spBeingEdited'], {
          ...currentParameterOutput,
          ls: stringMaxVal,
          trueValue: stringMaxVal,
          falseValue: stringMaxVal,
          defaultValue: stringMaxVal,
          taskId: stringMaxVal,
        })
      : setIn(['spBeingEdited'], {
          ...currentParameterOutput,
          ...(currentParameterOutput.taskId === stringMaxVal && {
            taskId: '3',
          }),
          ...(currentParameterOutput.falseValue === stringMaxVal && {
            falseValue: '0',
          }),
          ...(currentParameterOutput.trueValue === stringMaxVal && {
            trueValue: '1',
          }),
          ...(currentParameterOutput.defaultValue === stringMaxVal && {
            defaultValue: '0',
          }),
          ls:
            action.output.logicalSlot ||
            action.output.logical_slot ||
            action.output.timer_slot ||
            action.output.mlogic_slot_name ||
            action.output.mLogicSlotName ||
            translateNativeRowIdToLSValue(action.output),
        })

  return compose(set('selectedOutput', output), updateOutputProperties)(state)
}

function setMlogicOutputResult(state, action) {
  return state.mergeDeep({
    spBeingEdited: {
      output: {
        [action.resultSlot]: action.value,
      },
    },
  })
}

function setSlotEditDisplayMode(state, action) {
  return state.set('slotEditDisplayMode', action.newMode)
}

function setCurrSpFieldValidity(state, action) {
  switch (action.fieldName) {
    case 'spEditingDisabled':
      return state.set('spEditingDisabled', action.isValidForSaving)
    default:
      return updateSPErrors(state, action)
  }
}

const updateSPErrors = (state, action) => {
  const { isValidForSaving, fieldName, errorMsg, id } = action
  let newSPValue = {
    invalid: !isValidForSaving,
    errorMsg,
    id,
    fieldName,
  }

  let invalidSPValues = state.get('invalidSPValues')
    ? state.get('invalidSPValues').toJS()
    : false

  // clear out the error
  if (errorMsg === null) {
    invalidSPValues = invalidSPValues.filter((err) => err.id !== id)
  }

  if (!invalidSPValues || invalidSPValues.length === 0) {
    // there are no existing errors
    newSPValue = [newSPValue]
    return state.set('invalidSPValues', fromJS(newSPValue))
  }
  if (action.id && !invalidSPValues.some((value) => value.id === action.id)) {
    // there are existing errors, but no duplicates by id
    invalidSPValues.push(newSPValue)
  } else if (
    !invalidSPValues.some((value) => value.errorMsg === action.errorMsg)
  ) {
    // there are existing errors, but no duplicates
    invalidSPValues.push(newSPValue)
  } else {
    // This error message and id already exists
    /* eslint-disable no-lonely-if */
    if (action.id) {
      invalidSPValues = invalidSPValues.filter(
        (value) => value.id !== action.id
      )
      invalidSPValues.push(newSPValue)
    } else {
      // we have no reason to keep track of error states once they've cleared
      invalidSPValues = invalidSPValues.filter(
        (value) => value.errorMsg !== action.errorMsg
      )
    }
    /* eslint-enable no-lonely-if */
  }
  invalidSPValues = invalidSPValues.filter((value) => value.invalid === true)
  const newState = state.set('invalidSPValues', fromJS(invalidSPValues))
  return newState
}

/* eslint-disable no-nested-ternary */
const getSlotEditDisplayMode = (op) =>
  slotIsOpen(op)
    ? SLOT_EDIT_OPEN
    : slotIsShort(op)
    ? SLOT_EDIT_SHORT
    : SLOT_EDIT_INPUT
/* eslint-enable no-nested-ternary */

function getSupportParametersFromBatchImport(state, { supportParameters }) {
  const mappedSPs = supportParameters.reduce((dict, sp) => {
    const { uri } = sp
    const linkedRPUris = state.getIn(['dictionary', uri, 'linkedRPUris']) || []
    return {
      ...dict,
      [uri]: {
        linkedRPUris,
        ...sp,
      },
    }
  }, {})

  return state.mergeIn(['dictionary'], mappedSPs)
}

function getSupportParametersFromRP(state, { data }) {
  const { supportParameters } = data.entities
  return state.mergeDeepIn(['dictionary'], supportParameters)
}

function clearRps(state) {
  return state.set('rps', fromJS([]))
}

function getReferenceParameter(state, { data }) {
  const { supportParameters } = data.entities
  return state.mergeDeepIn(['dictionary'], supportParameters)
}

function setSlotProperties(state, action) {
  const selectedSlot = state.get('selectedSlot')
  const spBeingEditedUri = state.getIn(['spBeingEdited', 'uri'])
  const slotFromDictionary = state.getIn([
    'dictionary',
    spBeingEditedUri,
    'inputs',
    selectedSlot,
  ])

  if (
    slotFromDictionary &&
    slotFromDictionary.get('logicalSlot') === action.logicalSlot
  ) {
    action.value = slotFromDictionary.get('value')
  }

  if (mLogicInputSet.has(selectedSlot)) {
    return state.mergeIn(
      ['spBeingEdited', 'inputs', selectedSlot],
      fromJS(pick(['logicalSlot', 'operator', 'value'], action))
    )
  }

  return state.mergeIn(['spBeingEdited'], fromJS(omit(['type'], action)))
}

function setSelectedSlot(state, action) {
  return state.set('selectedSlot', action.id)
}
